import { useState } from "react";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

const factoryDashboardJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "工厂管理后台 | 皇冠鸿运商城",
  "description": "皇冠鸿运商城工厂管理后台，管理商品、订单、货款状态、收款账户、达人合作，查看数据概览。",
  "url": `${SITE_URL}/factory-dashboard`,
  "noIndex": true
};

type TabType = "overview" | "products" | "orders" | "payments" | "kol" | "profile" | "payout";

interface Product {
  id: number;
  name: string;
  price: number;
  stock: number;
  sales: number;
  status: "on_sale" | "off_shelf" | "pending";
  image: string;
  category: string;
  brand: string;
}

interface Order {
  id: string;
  product: string;
  buyer: string;
  qty: number;
  amount: number;
  status: "pending_ship" | "shipped" | "delivered" | "confirmed" | "dispute";
  date: string;
  escrow: "holding" | "released" | "frozen";
  trackingNo?: string;
  buyerEmail?: string;
  buyerPhone?: string;
  notified?: boolean;
}

const mockProducts: Product[] = [
  { id: 1, name: "极光户外防水冲锋衣 男女同款 三合一可拆卸", price: 688, stock: 240, sales: 1820, status: "on_sale", image: "https://public.readdy.ai/ai/img_res/a5fd9253b3ce63a3f8a65da362c948a3.jpg", category: "服装鞋包", brand: "极光户外" },
  { id: 2, name: "极光户外登山背包 60L 专业徒步防水大容量", price: 458, stock: 85, sales: 632, status: "on_sale", image: "https://readdy.ai/api/search-image?query=hiking%20backpack%2060L%20outdoor%20gear%20clean%20white%20background%20professional%20product%20photography&width=200&height=200&seq=fd2&orientation=squarish", category: "运动户外", brand: "极光户外" },
  { id: 3, name: "极光户外速干T恤 UPF50+ 防晒透气运动上衣", price: 168, stock: 520, sales: 3240, status: "on_sale", image: "https://readdy.ai/api/search-image?query=quick%20dry%20sports%20t-shirt%20outdoor%20UPF50%20clean%20white%20background%20product%20photography&width=200&height=200&seq=fd3&orientation=squarish", category: "运动户外", brand: "极光户外" },
  { id: 4, name: "极光户外登山鞋 防滑耐磨 GTX防水徒步鞋", price: 898, stock: 0, sales: 445, status: "off_shelf", image: "https://readdy.ai/api/search-image?query=hiking%20boots%20waterproof%20GTX%20outdoor%20shoes%20clean%20white%20background%20product%20photography&width=200&height=200&seq=fd4&orientation=squarish", category: "服装鞋包", brand: "极光户外" },
  { id: 5, name: "极光户外帐篷 2-3人 四季防风防雨露营帐篷", price: 1280, stock: 38, sales: 218, status: "pending", image: "https://readdy.ai/api/search-image?query=camping%20tent%202-3%20person%20outdoor%20gear%20clean%20white%20background%20product%20photography&width=200&height=200&seq=fd5&orientation=squarish", category: "运动户外", brand: "极光户外" },
];

const mockOrders: Order[] = [
  { id: "ORD-2026041101", product: "极光户外防水冲锋衣 男女同款", buyer: "王**", qty: 2, amount: 1376, status: "confirmed", date: "2026-04-08", escrow: "released", trackingNo: "SF1234567890", buyerEmail: "li**@163.com", buyerPhone: "+86 186****3456", notified: true },
  { id: "ORD-2026041102", product: "极光户外登山背包 60L", buyer: "李**", qty: 1, amount: 458, status: "shipped", date: "2026-04-09", escrow: "holding", trackingNo: "YT9876543210", buyerEmail: "wang**@gmail.com", buyerPhone: "+86 138****5678", notified: true },
  { id: "ORD-2026041103", product: "极光户外速干T恤 UPF50+", buyer: "张**", qty: 5, amount: 840, status: "pending_ship", date: "2026-04-10", escrow: "holding", buyerEmail: "zhang**@qq.com", buyerPhone: "+86 135****7890" },
  { id: "ORD-2026041104", product: "极光户外防水冲锋衣 男女同款", buyer: "陈**", qty: 1, amount: 688, status: "delivered", date: "2026-04-07", escrow: "holding", trackingNo: "JD5566778899", buyerEmail: "chen**@outlook.com", buyerPhone: "+86 139****9012", notified: true },
  { id: "ORD-2026041105", product: "极光户外速干T恤 UPF50+", buyer: "刘**", qty: 3, amount: 504, status: "dispute", date: "2026-04-05", escrow: "frozen", trackingNo: "SF1122334455", buyerPhone: "+86 177****2345", notified: true },
  { id: "ORD-2026041106", product: "极光户外登山背包 60L", buyer: "赵**", qty: 2, amount: 916, status: "confirmed", date: "2026-04-03", escrow: "released", trackingNo: "YT1122334455", buyerEmail: "zhao**@163.com", notified: true },
];

const statusLabel: Record<Order["status"], { text: string; color: string }> = {
  pending_ship: { text: "待发货", color: "bg-orange-100 text-orange-600" },
  shipped: { text: "已发货", color: "bg-blue-100 text-blue-600" },
  delivered: { text: "已送达", color: "bg-purple-100 text-purple-600" },
  confirmed: { text: "已确认", color: "bg-green-100 text-green-600" },
  dispute: { text: "纠纷中", color: "bg-red-100 text-red-600" },
};

const escrowLabel: Record<Order["escrow"], { text: string; color: string; icon: string }> = {
  holding: { text: "平台托管中", color: "text-amber-600", icon: "ri-lock-line" },
  released: { text: "已放款", color: "text-green-600", icon: "ri-check-double-line" },
  frozen: { text: "已冻结", color: "text-red-500", icon: "ri-error-warning-line" },
};

const productStatusLabel: Record<Product["status"], { text: string; color: string }> = {
  on_sale: { text: "销售中", color: "bg-green-100 text-green-600" },
  off_shelf: { text: "已下架", color: "bg-gray-100 text-gray-500" },
  pending: { text: "审核中", color: "bg-amber-100 text-amber-600" },
};

function OverviewTab() {
  const totalRevenue = mockOrders.filter(o => o.escrow === "released").reduce((s, o) => s + o.amount, 0);
  const holdingAmount = mockOrders.filter(o => o.escrow === "holding").reduce((s, o) => s + o.amount, 0);
  const totalSales = mockProducts.reduce((s, p) => s + p.sales, 0);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "已到账货款", value: `¥${totalRevenue.toLocaleString()}`, icon: "ri-money-cny-circle-line", color: "text-green-500", bg: "bg-green-50" },
          { label: "托管中货款", value: `¥${holdingAmount.toLocaleString()}`, icon: "ri-lock-line", color: "text-amber-500", bg: "bg-amber-50" },
          { label: "本月总销量", value: `${totalSales.toLocaleString()} 件`, icon: "ri-shopping-bag-line", color: "text-red-500", bg: "bg-red-50" },
          { label: "在售商品数", value: `${mockProducts.filter(p => p.status === "on_sale").length} 款`, icon: "ri-store-line", color: "text-indigo-500", bg: "bg-indigo-50" },
        ].map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl p-5 border border-gray-100">
            <div className={`w-10 h-10 flex items-center justify-center ${stat.bg} rounded-lg mb-3`}>
              <i className={`${stat.icon} ${stat.color} text-xl`}></i>
            </div>
            <p className="text-gray-400 text-xs mb-1">{stat.label}</p>
            <p className="text-gray-800 font-bold text-xl">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Escrow flow */}
      <div className="bg-white rounded-xl p-6 border border-gray-100">
        <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-shield-check-line text-amber-500"></i>
          货款托管流程说明
        </h3>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {[
            { step: "1", label: "买家付款", sub: "资金进入平台托管账户", icon: "ri-bank-card-line", color: "bg-blue-500" },
            { step: "2", label: "工厂发货", sub: "上传物流单号", icon: "ri-truck-line", color: "bg-orange-500" },
            { step: "3", label: "买家收货", sub: "确认商品质量", icon: "ri-checkbox-circle-line", color: "bg-purple-500" },
            { step: "4", label: "确认放款", sub: "买家点击确认", icon: "ri-thumb-up-line", color: "bg-green-500" },
            { step: "5", label: "货款到账", sub: "T+1工作日到账", icon: "ri-money-cny-circle-line", color: "bg-red-500" },
          ].map((s, idx) => (
            <div key={s.step} className="flex items-center gap-2 flex-shrink-0">
              <div className="text-center">
                <div className={`w-10 h-10 flex items-center justify-center ${s.color} rounded-full text-white mx-auto mb-2`}>
                  <i className={`${s.icon} text-sm`}></i>
                </div>
                <p className="text-gray-700 text-xs font-medium whitespace-nowrap">{s.label}</p>
                <p className="text-gray-400 text-xs whitespace-nowrap">{s.sub}</p>
              </div>
              {idx < 4 && <i className="ri-arrow-right-line text-gray-300 text-lg flex-shrink-0"></i>}
            </div>
          ))}
        </div>
        <div className="mt-4 bg-amber-50 rounded-lg p-3 text-xs text-amber-700 space-y-1">
          <p><i className="ri-information-line mr-1"></i>买家收货后7天内可发起质量纠纷，平台介入调查。纠纷期间货款冻结，待平台裁决后处理。</p>
          <p><i className="ri-percent-line mr-1"></i>平台收取 <strong>1.6% 交易佣金</strong>，放款时自动扣除。实际到账 = 订单金额 × 98.4%。</p>
        </div>
      </div>

      {/* Recent orders */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800">最近订单</h3>
          <span className="text-xs text-gray-400">最近6条</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["订单号", "商品", "买家", "金额", "订单状态", "货款状态"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {mockOrders.slice(0, 5).map(order => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{order.id}</td>
                  <td className="px-4 py-3 text-gray-700 max-w-[180px]">
                    <p className="line-clamp-1 text-xs">{order.product}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{order.buyer}</td>
                  <td className="px-4 py-3 text-gray-800 font-medium text-xs whitespace-nowrap">¥{order.amount.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusLabel[order.status].color}`}>
                      {statusLabel[order.status].text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs flex items-center gap-1 whitespace-nowrap ${escrowLabel[order.escrow].color}`}>
                      <i className={escrowLabel[order.escrow].icon}></i>
                      {escrowLabel[order.escrow].text}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [products, setProducts] = useState<Product[]>(mockProducts);
  const [form, setForm] = useState({ name: "", price: "", stock: "", category: "服装鞋包", brand: "", description: "" });
  const [submitDone, setSubmitDone] = useState(false);

  const handleToggle = (id: number) => {
    setProducts(prev => prev.map(p => p.id === id
      ? { ...p, status: p.status === "on_sale" ? "off_shelf" : "on_sale" }
      : p
    ));
  };

  const handleSubmit = () => {
    setSubmitDone(true);
    setTimeout(() => {
      setSubmitDone(false);
      setShowAddModal(false);
      setForm({ name: "", price: "", stock: "", category: "服装鞋包", brand: "", description: "" });
    }, 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-bold text-gray-800">我的商品</h3>
          <p className="text-gray-400 text-xs mt-0.5">仅可上架自有品牌商品，禁止仿冒他人品牌</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors"
        >
          <i className="ri-add-line"></i> 上架新商品
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["商品", "品牌", "价格", "库存", "销量", "状态", "操作"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {products.map(p => (
                <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={p.name} className="w-12 h-12 rounded-lg object-cover object-top flex-shrink-0" />
                      <div>
                        <p className="text-gray-700 text-xs font-medium line-clamp-2 max-w-[160px]">{p.name}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{p.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{p.brand}</td>
                  <td className="px-4 py-3 text-red-500 font-bold text-sm whitespace-nowrap">¥{p.price.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-medium ${p.stock === 0 ? "text-red-500" : p.stock < 50 ? "text-orange-500" : "text-gray-700"}`}>
                      {p.stock === 0 ? "缺货" : `${p.stock} 件`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{p.sales.toLocaleString()} 件</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${productStatusLabel[p.status].color}`}>
                      {productStatusLabel[p.status].text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button className="text-xs text-gray-500 hover:text-red-500 cursor-pointer whitespace-nowrap transition-colors">编辑</button>
                      {p.status !== "pending" && (
                        <button
                          onClick={() => handleToggle(p.id)}
                          className={`text-xs cursor-pointer whitespace-nowrap transition-colors ${p.status === "on_sale" ? "text-orange-500 hover:text-orange-600" : "text-green-500 hover:text-green-600"}`}
                        >
                          {p.status === "on_sale" ? "下架" : "上架"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add product modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-white font-bold text-lg">上架新商品</h3>
                <p className="text-white/70 text-xs mt-0.5">仅限自有品牌商品，平台将核验品牌证书</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700 flex items-start gap-2">
                <i className="ri-shield-check-line mt-0.5 flex-shrink-0"></i>
                <span>商品上架前需通过平台品牌核验，确认为您的注册商标商品。仿冒品牌将永久封号并移交司法机关。</span>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1.5">商品名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="请输入商品完整名称"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">售价（元）<span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={form.price}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                    placeholder="0.00"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">库存数量 <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    value={form.stock}
                    onChange={e => setForm(f => ({ ...f, stock: e.target.value }))}
                    placeholder="0"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">商品品类 <span className="text-red-500">*</span></label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors bg-white"
                  >
                    {["服装鞋包", "运动户外", "家用电器", "美妆护肤", "食品生鲜", "母婴玩具", "汽车用品"].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">品牌名称 <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={e => setForm(f => ({ ...f, brand: e.target.value }))}
                    placeholder="您的注册商标名称"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1.5">商品描述</label>
                <textarea
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value.slice(0, 500) }))}
                  placeholder="详细描述商品特点、材质、规格等信息..."
                  rows={4}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 transition-colors resize-none"
                />
                <p className="text-gray-400 text-xs mt-1 text-right">{form.description.length}/500</p>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1.5">商品图片</label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center cursor-pointer hover:border-red-300 transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg mx-auto mb-2">
                    <i className="ri-image-add-line text-gray-400 text-xl"></i>
                  </div>
                  <p className="text-gray-500 text-sm">点击上传商品图片</p>
                  <p className="text-gray-400 text-xs mt-1">支持 JPG、PNG，最多8张，每张不超过5MB</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 border border-gray-200 text-gray-500 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${submitDone ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
              >
                {submitDone ? <><i className="ri-check-line mr-1"></i>已提交审核</> : "提交审核"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersTab() {
  const [filter, setFilter] = useState<"all" | Order["status"]>("all");
  const [shipModal, setShipModal] = useState<Order | null>(null);
  const [trackingInput, setTrackingInput] = useState("");
  const [shipDone, setShipDone] = useState(false);

  const filtered = filter === "all" ? mockOrders : mockOrders.filter(o => o.status === filter);

  const handleShip = () => {
    setShipDone(true);
    setTimeout(() => {
      setShipDone(false);
      setShipModal(null);
      setTrackingInput("");
    }, 2000);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-5 flex-wrap">
        {[
          { key: "all", label: "全部" },
          { key: "pending_ship", label: "待发货" },
          { key: "shipped", label: "已发货" },
          { key: "delivered", label: "已送达" },
          { key: "confirmed", label: "已确认" },
          { key: "dispute", label: "纠纷中" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key as typeof filter)}
            className={`px-4 py-1.5 rounded-full text-sm cursor-pointer whitespace-nowrap transition-colors ${filter === tab.key ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["订单号", "商品", "买家", "数量", "金额", "日期", "订单状态", "货款状态", "操作"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(order => (
                <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{order.id}</td>
                  <td className="px-4 py-3 text-gray-700 text-xs max-w-[160px]">
                    <p className="line-clamp-2">{order.product}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{order.buyer}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{order.qty} 件</td>
                  <td className="px-4 py-3 text-gray-800 font-bold text-sm whitespace-nowrap">¥{order.amount.toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{order.date}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusLabel[order.status].color}`}>
                      {statusLabel[order.status].text}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className={`flex items-center gap-1 text-xs whitespace-nowrap ${escrowLabel[order.escrow].color}`}>
                      <i className={escrowLabel[order.escrow].icon}></i>
                      {escrowLabel[order.escrow].text}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {order.status === "pending_ship" && (
                      <button
                        onClick={() => setShipModal(order)}
                        className="text-xs bg-red-500 text-white px-3 py-1 rounded-lg cursor-pointer hover:bg-red-600 transition-colors whitespace-nowrap"
                      >
                        填写物流
                      </button>
                    )}
                    {order.trackingNo && order.status !== "pending_ship" && (
                      <span className="text-xs text-gray-400">{order.trackingNo}</span>
                    )}
                    {order.status === "dispute" && (
                      <button className="text-xs bg-orange-100 text-orange-600 px-3 py-1 rounded-lg cursor-pointer whitespace-nowrap">
                        查看纠纷
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ship modal */}
      {shipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShipModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-white font-bold text-lg">填写发货信息</h3>
                <p className="text-white/70 text-xs mt-0.5">订单号：{shipModal.id}</p>
              </div>
              <button onClick={() => setShipModal(null)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {!shipDone ? (
              <div className="p-6 space-y-4">
                {/* Buyer contact info */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-amber-800 font-semibold text-sm mb-2 flex items-center gap-1.5">
                    <i className="ri-notification-3-line text-amber-600"></i>
                    买家通知方式
                  </p>
                  <div className="space-y-1.5">
                    {shipModal.buyerEmail && (
                      <div className="flex items-center gap-2 text-amber-700 text-sm">
                        <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                          <i className="ri-mail-line text-xs"></i>
                        </div>
                        <span className="font-medium">邮箱：</span>
                        <span>{shipModal.buyerEmail}</span>
                      </div>
                    )}
                    {shipModal.buyerPhone && (
                      <div className="flex items-center gap-2 text-amber-700 text-sm">
                        <div className="w-6 h-6 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                          <i className="ri-smartphone-line text-xs"></i>
                        </div>
                        <span className="font-medium">手机：</span>
                        <span>{shipModal.buyerPhone}</span>
                      </div>
                    )}
                    {!shipModal.buyerEmail && !shipModal.buyerPhone && (
                      <p className="text-amber-600 text-xs">买家未留联系方式，将通过站内信通知</p>
                    )}
                  </div>
                  <p className="text-amber-600 text-xs mt-2 pt-2 border-t border-amber-200">
                    <i className="ri-information-line mr-1"></i>
                    上传物流单号后，系统将自动发送发货通知到买家的邮箱和手机
                  </p>
                </div>

                {/* Carrier */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">快递公司 <span className="text-red-500">*</span></label>
                  <select className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-red-400 bg-white cursor-pointer">
                    {["顺丰速运", "圆通速递", "中通快递", "韵达快递", "京东物流", "德邦快递", "EMS邮政", "DHL国际"].map(c => (
                      <option key={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Tracking number */}
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1.5">物流单号 <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <i className="ri-barcode-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    <input
                      type="text"
                      value={trackingInput}
                      onChange={e => setTrackingInput(e.target.value)}
                      placeholder="请输入快递单号"
                      className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-red-400 transition-colors font-mono"
                    />
                  </div>
                </div>

                {/* What happens next */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-gray-600 text-xs font-medium mb-2">确认发货后将自动执行：</p>
                  <div className="space-y-1.5">
                    {[
                      { icon: "ri-mail-send-line", text: "发送发货邮件到买家邮箱（含物流单号）", color: "text-red-400" },
                      { icon: "ri-message-2-line", text: "发送短信通知到买家手机", color: "text-green-500" },
                      { icon: "ri-lock-line", text: "货款继续由平台托管，等待买家确认", color: "text-amber-500" },
                    ].map(item => (
                      <div key={item.text} className="flex items-start gap-2">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className={`${item.icon} ${item.color} text-xs`}></i>
                        </div>
                        <span className="text-gray-500 text-xs leading-relaxed">{item.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setShipModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-3 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                    取消
                  </button>
                  <button
                    onClick={handleShip}
                    disabled={!trackingInput.trim()}
                    className={`flex-1 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${
                      !trackingInput.trim() ? "bg-gray-200 text-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600 text-white"
                    }`}
                  >
                    <i className="ri-truck-line mr-1"></i>确认发货并通知买家
                  </button>
                </div>
              </div>
            ) : (
              /* Success state */
              <div className="p-8 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <h3 className="text-gray-800 font-bold text-xl mb-2">发货成功！</h3>
                <p className="text-gray-500 text-sm mb-4">物流信息已上传，通知已发送给买家</p>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left space-y-2 mb-6">
                  <p className="text-green-700 text-xs font-semibold">通知已发送至：</p>
                  {shipModal.buyerEmail && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <i className="ri-mail-check-line"></i>
                      <span>{shipModal.buyerEmail}</span>
                      <span className="text-green-400 text-xs">✓ 邮件已发送</span>
                    </div>
                  )}
                  {shipModal.buyerPhone && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <i className="ri-message-2-line"></i>
                      <span>{shipModal.buyerPhone}</span>
                      <span className="text-green-400 text-xs">✓ 短信已发送</span>
                    </div>
                  )}
                  <div className="pt-2 border-t border-green-200">
                    <p className="text-green-600 text-xs">物流单号：<span className="font-mono font-bold">{trackingInput}</span></p>
                  </div>
                </div>
                <button
                  onClick={() => { setShipModal(null); setTrackingInput(""); }}
                  className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap transition-colors"
                >
                  返回订单列表
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentsTab() {
  const released = mockOrders.filter(o => o.escrow === "released");
  const holding = mockOrders.filter(o => o.escrow === "holding");
  const frozen = mockOrders.filter(o => o.escrow === "frozen");

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "已到账", amount: released.reduce((s, o) => s + o.amount, 0), count: released.length, color: "text-green-500", bg: "bg-green-50", icon: "ri-check-double-line" },
          { label: "托管中", amount: holding.reduce((s, o) => s + o.amount, 0), count: holding.length, color: "text-amber-500", bg: "bg-amber-50", icon: "ri-lock-line" },
          { label: "冻结中", amount: frozen.reduce((s, o) => s + o.amount, 0), count: frozen.length, color: "text-red-500", bg: "bg-red-50", icon: "ri-error-warning-line" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-9 h-9 flex items-center justify-center ${s.bg} rounded-lg`}>
                <i className={`${s.icon} ${s.color} text-lg`}></i>
              </div>
              <span className="text-gray-500 text-sm">{s.label}货款</span>
            </div>
            <p className={`text-2xl font-bold ${s.color}`}>¥{s.amount.toLocaleString()}</p>
            <p className="text-gray-400 text-xs mt-1">{s.count} 笔订单</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-gray-800">货款明细</h3>
          <span className="text-xs text-amber-600 bg-amber-50 border border-amber-100 px-3 py-1 rounded-full flex items-center gap-1">
            <i className="ri-percent-line"></i> 平台佣金 1.6%，放款时自动扣除
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">订单号</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">商品</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">订单金额</th>
                <th className="px-4 py-3 text-left text-xs text-orange-500 font-medium whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-percent-line"></i>佣金扣除 (1.6%)
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs text-green-600 font-medium whitespace-nowrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-money-cny-circle-line"></i>实际到账
                  </span>
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">订单日期</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">货款状态</th>
                <th className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">到账时间</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {mockOrders.map(order => {
                const commission = order.amount * 0.016;
                const netAmount = order.amount * 0.984;
                return (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{order.id}</td>
                    <td className="px-4 py-3 text-gray-700 text-xs max-w-[160px]">
                      <p className="line-clamp-1">{order.product}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-800 font-bold text-sm whitespace-nowrap">
                      ¥{order.amount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-orange-500 font-medium text-sm">
                        -¥{commission.toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {order.escrow === "frozen" ? (
                        <span className="text-red-400 text-xs">冻结中</span>
                      ) : (
                        <span className="text-green-600 font-bold text-sm">
                          ¥{netAmount.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{order.date}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs flex items-center gap-1 whitespace-nowrap ${escrowLabel[order.escrow].color}`}>
                        <i className={escrowLabel[order.escrow].icon}></i>
                        {escrowLabel[order.escrow].text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {order.escrow === "released" ? (
                        <span className="text-green-500 flex items-center gap-1">
                          <i className="ri-check-line"></i>已到账
                        </span>
                      ) : order.escrow === "frozen" ? (
                        <span className="text-red-400">纠纷处理中</span>
                      ) : (
                        <span className="text-gray-400">买家确认后T+1</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-gray-600 text-xs font-bold whitespace-nowrap">合计（全部订单）</td>
                <td className="px-4 py-3 text-gray-800 font-bold text-sm whitespace-nowrap">
                  ¥{mockOrders.reduce((s, o) => s + o.amount, 0).toLocaleString()}
                </td>
                <td className="px-4 py-3 text-orange-500 font-bold text-sm whitespace-nowrap">
                  -¥{(mockOrders.reduce((s, o) => s + o.amount, 0) * 0.016).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-green-600 font-bold text-sm whitespace-nowrap">
                  ¥{(mockOrders.reduce((s, o) => s + o.amount, 0) * 0.984).toFixed(2)}
                </td>
                <td colSpan={3} className="px-4 py-3 text-gray-400 text-xs">
                  实际到账 = 订单金额 × 98.4%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Commission breakdown */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <h4 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-percent-line text-amber-500"></i>
          平台佣金说明（1.6% / 笔）
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          {[
            { label: "订单总额", value: `¥${mockOrders.reduce((s, o) => s + o.amount, 0).toLocaleString()}`, sub: "所有订单合计", color: "text-gray-800", bg: "bg-gray-50" },
            { label: "平台佣金 (1.6%)", value: `¥${(mockOrders.reduce((s, o) => s + o.amount, 0) * 0.016).toFixed(2)}`, sub: "自动从货款中扣除", color: "text-orange-500", bg: "bg-orange-50" },
            { label: "实际到账", value: `¥${(mockOrders.reduce((s, o) => s + o.amount, 0) * 0.984).toFixed(2)}`, sub: "订单金额 × 98.4%", color: "text-green-600", bg: "bg-green-50" },
          ].map(s => (
            <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
              <p className="text-gray-400 text-xs mb-1">{s.label}</p>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-gray-400 text-xs mt-1">{s.sub}</p>
            </div>
          ))}
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-xs text-amber-700">
          <i className="ri-lightbulb-line mr-1"></i>
          <strong>计算示例：</strong>订单金额 ¥1,000 → 平台佣金 ¥16（1.6%）→ 工厂实际到账 ¥984。佣金在货款放款时自动扣除，与月费 ¥5 分开计算。
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
          <i className="ri-information-line"></i>
          货款到账说明
        </h4>
        <ul className="space-y-2 text-sm text-amber-700">
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>买家确认收货并验证质量后，货款将在T+1工作日内到账</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>买家收货后7天内可发起质量纠纷，纠纷期间货款冻结</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>平台收取 <strong>1.6% 交易佣金</strong>，到账金额为订单金额 × 98.4%</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>如买家超过7天未确认，系统将自动确认并释放货款</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0"></i>月费 ¥5 与交易佣金分开计算，互不影响</li>
        </ul>
      </div>
    </div>
  );
}

const mockKols = [
  {
    id: "INF-20250001",
    name: "好物猎人雨姐",
    avatar: "https://readdy.ai/api/search-image?query=young%20attractive%20Asian%20female%20social%20media%20influencer%20smiling%20portrait%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20confident%20stylish%20expression&width=80&height=80&seq=kol_av1&orientation=squarish",
    platforms: ["抖音", "TikTok", "YouTube"],
    followers: "307万",
    totalOrders: 312,
    totalSales: 89640,
    myCommission: 10720,
    status: "合作中",
    joinDate: "2025-09-12",
  },
  {
    id: "INF-20250042",
    name: "健身狂人阿壮",
    avatar: "https://readdy.ai/api/search-image?query=young%20Chinese%20male%20outdoor%20sports%20influencer%20smiling%20portrait%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20athletic%20confident%20expression&width=80&height=80&seq=kol_av2&orientation=squarish",
    platforms: ["抖音", "微信视频号"],
    followers: "88万",
    totalOrders: 156,
    totalSales: 42800,
    myCommission: 5136,
    status: "合作中",
    joinDate: "2025-11-03",
  },
  {
    id: "INF-20250078",
    name: "时尚辣妈苏苏",
    avatar: "https://readdy.ai/api/search-image?query=young%20Chinese%20female%20fashion%20lifestyle%20influencer%20smiling%20portrait%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20trendy%20stylish%20expression&width=80&height=80&seq=kol_av3&orientation=squarish",
    platforms: ["TikTok", "YouTube"],
    followers: "215万",
    totalOrders: 89,
    totalSales: 28900,
    myCommission: 3468,
    status: "合作中",
    joinDate: "2026-01-20",
  },
  {
    id: "INF-20250099",
    name: "数码测评老王",
    avatar: "https://readdy.ai/api/search-image?query=young%20Chinese%20male%20tech%20reviewer%20influencer%20smiling%20portrait%2C%20professional%20studio%20lighting%2C%20clean%20white%20background%2C%20smart%20confident%20expression&width=80&height=80&seq=kol_av4&orientation=squarish",
    platforms: ["抖音"],
    followers: "52万",
    totalOrders: 34,
    totalSales: 9800,
    myCommission: 1176,
    status: "待审核",
    joinDate: "2026-03-15",
  },
];

const platformColors: Record<string, string> = {
  "抖音": "bg-gray-900",
  "TikTok": "bg-gray-700",
  "微信视频号": "bg-green-600",
  "YouTube": "bg-red-600",
};

function KolTab() {
  const [commissionRate, setCommissionRate] = useState(12);
  const [editingRate, setEditingRate] = useState(false);
  const [tempRate, setTempRate] = useState("12");
  const [saveDone, setSaveDone] = useState(false);
  const [selectedKol, setSelectedKol] = useState<typeof mockKols[0] | null>(null);

  const handleSaveRate = () => {
    const val = Math.min(50, Math.max(1, Number(tempRate)));
    setCommissionRate(val);
    setSaveDone(true);
    setTimeout(() => {
      setSaveDone(false);
      setEditingRate(false);
    }, 1800);
  };

  const totalKolSales = mockKols.filter(k => k.status === "合作中").reduce((s, k) => s + k.totalSales, 0);
  const totalKolOrders = mockKols.filter(k => k.status === "合作中").reduce((s, k) => s + k.totalOrders, 0);
  const totalKolCommission = mockKols.filter(k => k.status === "合作中").reduce((s, k) => s + k.myCommission, 0);

  return (
    <div className="space-y-6">
      {/* Commission rate setting */}
      <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
              <i className="ri-percent-line text-amber-500"></i>
              达人佣金比例设置
            </h3>
            <p className="text-gray-500 text-sm mt-1">
              您设定的佣金比例将对所有合作达人生效，达人每促成一笔订单，系统自动按此比例结算佣金
            </p>
          </div>
          <div className="flex-shrink-0">
            {!editingRate ? (
              <div className="text-center">
                <div className="bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl px-8 py-4 text-white mb-2">
                  <p className="text-white/70 text-xs">当前佣金比例</p>
                  <p className="text-4xl font-bold">{commissionRate}%</p>
                </div>
                <button
                  onClick={() => { setEditingRate(true); setTempRate(String(commissionRate)); }}
                  className="text-amber-600 text-sm font-medium cursor-pointer hover:text-amber-700 flex items-center gap-1 mx-auto"
                >
                  <i className="ri-edit-line"></i>修改比例
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-4 border border-amber-200 min-w-[200px]">
                <p className="text-gray-600 text-xs font-medium mb-2">设置新的佣金比例（1%–50%）</p>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={tempRate}
                    onChange={e => setTempRate(e.target.value)}
                    className="flex-1 border border-amber-300 rounded-lg px-3 py-2 text-xl font-bold text-amber-600 outline-none text-center"
                  />
                  <span className="text-amber-600 font-bold text-xl">%</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditingRate(false)}
                    className="flex-1 border border-gray-200 text-gray-500 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleSaveRate}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${saveDone ? "bg-green-500 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}
                  >
                    {saveDone ? <><i className="ri-check-line mr-1"></i>已保存</> : "保存"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Rate guide */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { range: "5%–8%", label: "基础档", desc: "适合高客单价商品", color: "bg-gray-100 text-gray-600" },
            { range: "10%–12%", label: "标准档", desc: "平台平均水平", color: "bg-amber-100 text-amber-700" },
            { range: "15%–18%", label: "优质档", desc: "吸引头部达人", color: "bg-orange-100 text-orange-700" },
            { range: "20%+", label: "爆款档", desc: "快速打爆新品", color: "bg-red-100 text-red-600" },
          ].map(g => (
            <div key={g.range} className={`${g.color} rounded-xl p-3 text-center`}>
              <p className="font-bold text-lg">{g.range}</p>
              <p className="font-semibold text-xs">{g.label}</p>
              <p className="text-xs opacity-70 mt-0.5">{g.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* KOL stats summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "达人带货总销售额", value: `¥${totalKolSales.toLocaleString()}`, icon: "ri-shopping-bag-line", color: "text-amber-500", bg: "bg-amber-50" },
          { label: "达人带货总订单", value: `${totalKolOrders} 单`, icon: "ri-file-list-3-line", color: "text-green-500", bg: "bg-green-50" },
          { label: "已支付达人佣金", value: `¥${totalKolCommission.toLocaleString()}`, icon: "ri-money-cny-circle-line", color: "text-red-500", bg: "bg-red-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-100">
            <div className={`w-10 h-10 flex items-center justify-center ${s.bg} rounded-lg mb-3`}>
              <i className={`${s.icon} ${s.color} text-xl`}></i>
            </div>
            <p className="text-gray-400 text-xs mb-1">{s.label}</p>
            <p className="text-gray-800 font-bold text-xl">{s.value}</p>
          </div>
        ))}
      </div>

      {/* KOL list */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-800">合作达人列表</h3>
          <span className="text-gray-400 text-xs">{mockKols.length} 位达人</span>
        </div>
        <div className="divide-y divide-gray-50">
          {mockKols.map(kol => (
            <div key={kol.id} className="px-6 py-4 hover:bg-amber-50/30 transition-colors">
              <div className="flex items-center gap-4 flex-wrap">
                <img
                  src={kol.avatar}
                  alt={kol.name}
                  className="w-12 h-12 rounded-xl object-cover object-top flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-gray-800 font-bold text-sm">{kol.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${kol.status === "合作中" ? "bg-green-100 text-green-600" : "bg-orange-100 text-orange-500"}`}>
                      {kol.status}
                    </span>
                    <span className="text-gray-400 text-xs">{kol.id}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {kol.platforms.map(p => (
                      <span key={p} className={`inline-flex items-center gap-1 text-white text-xs px-2 py-0.5 rounded-full ${platformColors[p] ?? "bg-gray-500"}`}>
                        {p}
                      </span>
                    ))}
                    <span className="text-gray-400 text-xs">粉丝 {kol.followers}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-400 text-xs">加入 {kol.joinDate}</span>
                  </div>
                </div>
                {kol.status === "合作中" && (
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="text-center">
                      <p className="text-gray-800 font-bold text-base">{kol.totalOrders}</p>
                      <p className="text-gray-400 text-xs">带货订单</p>
                    </div>
                    <div className="text-center">
                      <p className="text-gray-800 font-bold text-base">¥{(kol.totalSales / 1000).toFixed(1)}k</p>
                      <p className="text-gray-400 text-xs">带货销售额</p>
                    </div>
                    <div className="text-center">
                      <p className="text-amber-600 font-bold text-base">¥{kol.myCommission.toLocaleString()}</p>
                      <p className="text-gray-400 text-xs">已付佣金</p>
                    </div>
                    <button
                      onClick={() => setSelectedKol(kol)}
                      className="text-xs bg-amber-50 border border-amber-200 text-amber-600 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-eye-line mr-1"></i>查看详情
                    </button>
                  </div>
                )}
                {kol.status === "待审核" && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button className="text-xs bg-green-500 text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-green-600 transition-colors whitespace-nowrap">
                      <i className="ri-check-line mr-1"></i>通过
                    </button>
                    <button className="text-xs bg-gray-100 text-gray-500 px-3 py-1.5 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap">
                      拒绝
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KOL detail modal */}
      {selectedKol && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedKol(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-amber-400 to-yellow-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-gray-800 font-bold text-lg">达人详情</h3>
              <button onClick={() => setSelectedKol(null)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 mb-5">
                <img src={selectedKol.avatar} alt={selectedKol.name} className="w-16 h-16 rounded-xl object-cover object-top flex-shrink-0" />
                <div>
                  <p className="text-gray-800 font-bold text-lg">{selectedKol.name}</p>
                  <p className="text-gray-400 text-xs">{selectedKol.id}</p>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {selectedKol.platforms.map(p => (
                      <span key={p} className={`text-white text-xs px-2 py-0.5 rounded-full ${platformColors[p] ?? "bg-gray-500"}`}>{p}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {[
                  { label: "粉丝总数", value: selectedKol.followers },
                  { label: "合作开始", value: selectedKol.joinDate },
                  { label: "带货订单", value: `${selectedKol.totalOrders} 单` },
                  { label: "带货销售额", value: `¥${selectedKol.totalSales.toLocaleString()}` },
                  { label: "已付佣金", value: `¥${selectedKol.myCommission.toLocaleString()}` },
                  { label: "当前佣金比例", value: `${commissionRate}%` },
                ].map(item => (
                  <div key={item.label} className="bg-amber-50 rounded-xl p-3">
                    <p className="text-gray-400 text-xs">{item.label}</p>
                    <p className="text-gray-800 font-bold text-sm mt-0.5">{item.value}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setSelectedKol(null)}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Payout Account Tab ────────────────────────────────────────────────────
function PayoutTab() {
  type AccountType = "bank" | "alipay" | "wechat";
  interface PayoutAccount {
    id: string;
    type: AccountType;
    label: string;
    detail: string;
    holder: string;
    isDefault: boolean;
    verified: boolean;
  }

  const [accounts, setAccounts] = useState<PayoutAccount[]>([
    { id: "acc1", type: "bank", label: "中国工商银行", detail: "**** **** **** 8821", holder: "极光户外装备有限公司", isDefault: true, verified: true },
    { id: "acc2", type: "alipay", label: "支付宝", detail: "jiguang_outdoor@alipay.com", holder: "极光户外装备有限公司", isDefault: false, verified: true },
  ]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addType, setAddType] = useState<AccountType>("bank");
  const [form, setForm] = useState({ bankName: "", cardNumber: "", holder: "", branch: "", alipayAccount: "", wechatAccount: "" });
  const [addDone, setAddDone] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const typeConfig: Record<AccountType, { icon: string; color: string; bg: string; label: string }> = {
    bank: { icon: "ri-bank-line", color: "text-indigo-600", bg: "bg-indigo-50", label: "银行卡" },
    alipay: { icon: "ri-alipay-line", color: "text-sky-600", bg: "bg-sky-50", label: "支付宝" },
    wechat: { icon: "ri-wechat-pay-line", color: "text-green-600", bg: "bg-green-50", label: "微信收款" },
  };

  const handleSetDefault = (id: string) => {
    setAccounts(prev => prev.map(a => ({ ...a, isDefault: a.id === id })));
  };

  const handleDelete = (id: string) => {
    setAccounts(prev => prev.filter(a => a.id !== id));
    setDeleteConfirm(null);
  };

  const handleAdd = () => {
    setAddDone(true);
    setTimeout(() => {
      const newAcc: PayoutAccount = {
        id: `acc${Date.now()}`,
        type: addType,
        label: addType === "bank" ? form.bankName || "新银行卡" : addType === "alipay" ? "支付宝" : "微信收款",
        detail: addType === "bank"
          ? `**** **** **** ${form.cardNumber.slice(-4) || "0000"}`
          : addType === "alipay" ? form.alipayAccount : form.wechatAccount,
        holder: form.holder || "极光户外装备有限公司",
        isDefault: accounts.length === 0,
        verified: false,
      };
      setAccounts(prev => [...prev, newAcc]);
      setAddDone(false);
      setShowAddModal(false);
      setForm({ bankName: "", cardNumber: "", holder: "", branch: "", alipayAccount: "", wechatAccount: "" });
    }, 1800);
  };

  const totalPending = 2292;
  const nextPayoutDate = "2026-04-15";

  return (
    <div className="space-y-6">
      {/* Payout summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "待到账货款", value: `¥${totalPending.toLocaleString()}`, sub: "买家确认后T+1到账", icon: "ri-time-line", color: "text-amber-500", bg: "bg-amber-50" },
          { label: "下次预计到账", value: nextPayoutDate, sub: "工作日自动打款", icon: "ri-calendar-check-line", color: "text-indigo-500", bg: "bg-indigo-50" },
          { label: "本月已到账", value: "¥2,294.40", sub: "扣除1.6%佣金后", icon: "ri-check-double-line", color: "text-green-500", bg: "bg-green-50" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-5 border border-gray-100">
            <div className={`w-9 h-9 flex items-center justify-center ${s.bg} rounded-lg mb-3`}>
              <i className={`${s.icon} ${s.color} text-lg`}></i>
            </div>
            <p className="text-gray-400 text-xs mb-1">{s.label}</p>
            <p className="text-gray-800 font-bold text-lg">{s.value}</p>
            <p className="text-gray-400 text-xs mt-0.5">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Account list */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-800">收款账户管理</h3>
            <p className="text-gray-400 text-xs mt-0.5">货款将自动打入默认收款账户，最多可添加 5 个账户</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={accounts.length >= 5}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${accounts.length >= 5 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-red-500 hover:bg-red-600 text-white"}`}
          >
            <i className="ri-add-line"></i>添加账户
          </button>
        </div>

        <div className="divide-y divide-gray-50">
          {accounts.map(acc => {
            const cfg = typeConfig[acc.type];
            return (
              <div key={acc.id} className={`px-6 py-5 flex items-center gap-4 flex-wrap ${acc.isDefault ? "bg-green-50/40" : ""}`}>
                <div className={`w-12 h-12 flex items-center justify-center ${cfg.bg} rounded-xl flex-shrink-0`}>
                  <i className={`${cfg.icon} ${cfg.color} text-2xl`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-gray-800 font-bold text-sm">{acc.label}</span>
                    {acc.isDefault && (
                      <span className="bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                        <i className="ri-check-line mr-0.5"></i>默认收款
                      </span>
                    )}
                    {acc.verified ? (
                      <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-verified-badge-line mr-0.5 text-green-500"></i>已验证
                      </span>
                    ) : (
                      <span className="bg-orange-50 text-orange-500 text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                        <i className="ri-time-line mr-0.5"></i>验证中
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600 text-sm font-mono">{acc.detail}</p>
                  <p className="text-gray-400 text-xs mt-0.5">持有人：{acc.holder}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {!acc.isDefault && (
                    <button
                      onClick={() => handleSetDefault(acc.id)}
                      className="text-xs border border-gray-200 text-gray-600 px-3 py-1.5 rounded-lg cursor-pointer hover:border-green-400 hover:text-green-600 transition-colors whitespace-nowrap"
                    >
                      设为默认
                    </button>
                  )}
                  {!acc.isDefault && (
                    <button
                      onClick={() => setDeleteConfirm(acc.id)}
                      className="text-xs border border-gray-200 text-gray-400 px-3 py-1.5 rounded-lg cursor-pointer hover:border-red-300 hover:text-red-500 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          {accounts.length === 0 && (
            <div className="px-6 py-12 text-center">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-3">
                <i className="ri-bank-card-line text-gray-400 text-2xl"></i>
              </div>
              <p className="text-gray-500 text-sm font-medium">暂无收款账户</p>
              <p className="text-gray-400 text-xs mt-1">请添加银行卡或支付宝账户以接收货款</p>
            </div>
          )}
        </div>
      </div>

      {/* Payout history table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-gray-800">提现到账记录</h3>
            <p className="text-gray-400 text-xs mt-0.5">每笔货款放款后 T+1 工作日自动打入默认收款账户</p>
          </div>
          <div className="flex items-center gap-2">
            <select className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 outline-none focus:border-red-400 bg-white cursor-pointer">
              <option>全部状态</option>
              <option>已到账</option>
              <option>处理中</option>
              <option>冻结</option>
            </select>
            <select className="border border-gray-200 rounded-lg px-3 py-1.5 text-xs text-gray-600 outline-none focus:border-red-400 bg-white cursor-pointer">
              <option>近30天</option>
              <option>近90天</option>
              <option>近半年</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {["流水号", "关联订单", "订单金额", "平台佣金(1.6%)", "实际到账", "到账账户", "到账时间", "状态"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs text-gray-500 font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {[
                { txId: "TXN-20260410-001", orderId: "ORD-2026041101", amount: 1376, account: "工商银行 ****8821", arrivedAt: "2026-04-10 09:15", status: "arrived" },
                { txId: "TXN-20260408-003", orderId: "ORD-2026041106", amount: 916, account: "工商银行 ****8821", arrivedAt: "2026-04-08 10:02", status: "arrived" },
                { txId: "TXN-20260407-002", orderId: "ORD-2026041102", amount: 458, account: "工商银行 ****8821", arrivedAt: "—", status: "processing" },
                { txId: "TXN-20260405-001", orderId: "ORD-2026041104", amount: 688, account: "工商银行 ****8821", arrivedAt: "—", status: "processing" },
                { txId: "TXN-20260403-005", orderId: "ORD-2026041105", amount: 504, account: "—", arrivedAt: "—", status: "frozen" },
                { txId: "TXN-20260328-002", orderId: "ORD-2026032801", amount: 2560, account: "工商银行 ****8821", arrivedAt: "2026-03-29 08:45", status: "arrived" },
                { txId: "TXN-20260320-001", orderId: "ORD-2026032001", amount: 1188, account: "支付宝 jiguang_**", arrivedAt: "2026-03-21 11:30", status: "arrived" },
                { txId: "TXN-20260315-003", orderId: "ORD-2026031501", amount: 3440, account: "工商银行 ****8821", arrivedAt: "2026-03-16 09:10", status: "arrived" },
              ].map(row => {
                const commission = row.amount * 0.016;
                const net = row.amount * 0.984;
                const statusMap = {
                  arrived: { text: "已到账", color: "bg-green-100 text-green-600", icon: "ri-check-double-line" },
                  processing: { text: "处理中", color: "bg-amber-100 text-amber-600", icon: "ri-time-line" },
                  frozen: { text: "冻结中", color: "bg-red-100 text-red-500", icon: "ri-error-warning-line" },
                };
                const s = statusMap[row.status as keyof typeof statusMap];
                return (
                  <tr key={row.txId} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono whitespace-nowrap">{row.txId}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">{row.orderId}</td>
                    <td className="px-4 py-3 text-gray-800 font-bold text-sm whitespace-nowrap">¥{row.amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-orange-500 text-sm whitespace-nowrap">-¥{commission.toFixed(2)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {row.status === "frozen"
                        ? <span className="text-red-400 text-xs">冻结中</span>
                        : <span className="text-green-600 font-bold text-sm">¥{net.toFixed(2)}</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{row.account}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{row.arrivedAt}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${s.color}`}>
                        <i className={s.icon}></i>{s.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-gray-600 text-xs font-bold whitespace-nowrap">合计（已到账）</td>
                <td className="px-4 py-3 text-gray-800 font-bold text-sm whitespace-nowrap">¥{(1376+916+2560+1188+3440).toLocaleString()}</td>
                <td className="px-4 py-3 text-orange-500 font-bold text-sm whitespace-nowrap">-¥{((1376+916+2560+1188+3440)*0.016).toFixed(2)}</td>
                <td className="px-4 py-3 text-green-600 font-bold text-sm whitespace-nowrap">¥{((1376+916+2560+1188+3440)*0.984).toFixed(2)}</td>
                <td colSpan={3} className="px-4 py-3 text-gray-400 text-xs">5 笔已到账</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Security tips */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
        <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
          <i className="ri-shield-check-line text-amber-500"></i>
          收款账户安全须知
        </h4>
        <ul className="space-y-2 text-sm text-amber-700">
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0 text-amber-500"></i>收款账户须与营业执照主体一致，个人账户需提供身份证核验</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0 text-amber-500"></i>新添加账户需 1-3 个工作日完成验证，验证期间不影响现有账户收款</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0 text-amber-500"></i>修改默认收款账户后，次日起生效的货款将打入新账户</li>
          <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line mt-0.5 flex-shrink-0 text-amber-500"></i>如发现账户异常，请立即联系平台客服冻结账户</li>
        </ul>
      </div>

      {/* Add account modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowAddModal(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-white font-bold text-lg">添加收款账户</h3>
                <p className="text-white/70 text-xs mt-0.5">账户须与营业执照主体一致</p>
              </div>
              <button onClick={() => setShowAddModal(false)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer">
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-5">
              {/* Type selector */}
              <div>
                <p className="text-gray-700 text-sm font-medium mb-3">选择账户类型</p>
                <div className="grid grid-cols-3 gap-3">
                  {(["bank", "alipay", "wechat"] as AccountType[]).map(t => {
                    const cfg = typeConfig[t];
                    return (
                      <button
                        key={t}
                        onClick={() => setAddType(t)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all cursor-pointer ${addType === t ? "border-red-400 bg-red-50" : "border-gray-200 hover:border-gray-300 bg-white"}`}
                      >
                        <div className={`w-10 h-10 flex items-center justify-center ${cfg.bg} rounded-lg`}>
                          <i className={`${cfg.icon} ${cfg.color} text-xl`}></i>
                        </div>
                        <span className="text-gray-700 text-xs font-medium">{cfg.label}</span>
                        {addType === t && <div className="w-4 h-4 flex items-center justify-center bg-red-500 rounded-full"><i className="ri-check-line text-white text-xs"></i></div>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Bank fields */}
              {addType === "bank" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">开户银行 <span className="text-red-500">*</span></label>
                    <select
                      value={form.bankName}
                      onChange={e => setForm(f => ({ ...f, bankName: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 bg-white"
                    >
                      <option value="">请选择银行</option>
                      {["中国工商银行", "中国建设银行", "中国农业银行", "中国银行", "招商银行", "交通银行", "浦发银行", "中信银行", "光大银行", "民生银行"].map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">银行卡号 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.cardNumber}
                      onChange={e => setForm(f => ({ ...f, cardNumber: e.target.value.replace(/\D/g, "").slice(0, 19) }))}
                      placeholder="请输入银行卡号"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">开户名称 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.holder}
                      onChange={e => setForm(f => ({ ...f, holder: e.target.value }))}
                      placeholder="与营业执照一致的企业名称"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                    />
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">开户支行</label>
                    <input
                      type="text"
                      value={form.branch}
                      onChange={e => setForm(f => ({ ...f, branch: e.target.value }))}
                      placeholder="如：杭州余杭支行"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-400"
                    />
                  </div>
                </div>
              )}

              {/* Alipay fields */}
              {addType === "alipay" && (
                <div className="space-y-3">
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-alipay-line text-sky-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sky-800 font-semibold text-sm">支付宝企业账户</p>
                      <p className="text-sky-600 text-xs mt-0.5">请使用与营业执照绑定的企业支付宝账户，个人账户需额外实名认证</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">支付宝账号（手机号或邮箱）<span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.alipayAccount}
                      onChange={e => setForm(f => ({ ...f, alipayAccount: e.target.value }))}
                      placeholder="请输入支付宝账号"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-sky-400"
                    />
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">账户持有人 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.holder}
                      onChange={e => setForm(f => ({ ...f, holder: e.target.value }))}
                      placeholder="与营业执照一致的企业名称"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-sky-400"
                    />
                  </div>
                </div>
              )}

              {/* WeChat fields */}
              {addType === "wechat" && (
                <div className="space-y-3">
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                    <i className="ri-wechat-pay-line text-green-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-green-800 font-semibold text-sm">微信商户收款</p>
                      <p className="text-green-600 text-xs mt-0.5">需绑定微信支付商户号，请确保已完成微信支付商户认证</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">微信商户号 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.wechatAccount}
                      onChange={e => setForm(f => ({ ...f, wechatAccount: e.target.value.replace(/\D/g, "") }))}
                      placeholder="请输入微信支付商户号"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs font-medium block mb-1.5">商户名称 <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      value={form.holder}
                      onChange={e => setForm(f => ({ ...f, holder: e.target.value }))}
                      placeholder="与营业执照一致的企业名称"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-green-400"
                    />
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-3 text-xs text-gray-500 flex items-start gap-2">
                <i className="ri-information-line text-gray-400 flex-shrink-0 mt-0.5"></i>
                <span>添加后需 1-3 个工作日完成账户验证。验证通过后，货款将自动打入此账户。</span>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex gap-3 flex-shrink-0">
              <button onClick={() => setShowAddModal(false)} className="flex-1 border border-gray-200 text-gray-500 py-2.5 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap transition-colors">
                取消
              </button>
              <button
                onClick={handleAdd}
                className={`flex-1 py-2.5 rounded-lg text-sm font-bold cursor-pointer whitespace-nowrap transition-all ${addDone ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"}`}
              >
                {addDone ? <><i className="ri-check-line mr-1"></i>已提交验证</> : "提交添加"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4 text-center" onClick={e => e.stopPropagation()}>
            <div className="w-14 h-14 flex items-center justify-center bg-red-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-2xl"></i>
            </div>
            <h3 className="font-bold text-gray-800 text-lg mb-2">确认删除账户？</h3>
            <p className="text-gray-500 text-sm mb-6">删除后该账户将无法接收货款，此操作不可撤销。</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2.5 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-colors">确认删除</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileTab() {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-red-500 to-orange-500 rounded-xl text-white text-2xl font-bold flex-shrink-0">
            极
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-lg">极光户外装备有限公司</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                <i className="ri-verified-badge-line"></i> 已认证工厂
              </span>
              <span className="bg-amber-100 text-amber-600 text-xs px-2 py-0.5 rounded-full">付费会员</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { label: "营业执照", value: "91110108MA01XXXXX", icon: "ri-file-text-line", verified: true },
            { label: "生产许可证", value: "XK16-001-XXXXX", icon: "ri-file-shield-line", verified: true },
            { label: "商标注册证", value: "极光户外 第25类", icon: "ri-award-line", verified: true },
            { label: "工厂地址", value: "浙江省杭州市余杭区仓前街道", icon: "ri-map-pin-line", verified: true },
            { label: "仓库面积", value: "约 3,200 m²", icon: "ri-building-line", verified: true },
            { label: "入驻时间", value: "2025年11月15日", icon: "ri-calendar-check-line", verified: false },
          ].map(item => (
            <div key={item.label} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                <i className={`${item.icon} text-red-400`}></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 text-xs">{item.label}</p>
                <p className="text-gray-700 text-sm font-medium mt-0.5 truncate">{item.value}</p>
              </div>
              {item.verified && (
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-verified-badge-fill text-green-500 text-sm"></i>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h4 className="font-bold text-gray-800 mb-4">工厂实景照片</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "生产车间", img: "https://readdy.ai/api/search-image?query=modern%20factory%20production%20workshop%20interior%20manufacturing%20equipment%20clean%20organized%20industrial%20facility&width=300&height=200&seq=fw1&orientation=landscape" },
            { label: "仓储中心", img: "https://readdy.ai/api/search-image?query=large%20warehouse%20storage%20facility%20organized%20shelves%20inventory%20management%20logistics%20center&width=300&height=200&seq=fw2&orientation=landscape" },
            { label: "质检区域", img: "https://readdy.ai/api/search-image?query=quality%20control%20inspection%20area%20factory%20workers%20checking%20products%20professional%20industrial&width=300&height=200&seq=fw3&orientation=landscape" },
            { label: "工厂门牌", img: "https://readdy.ai/api/search-image?query=factory%20building%20entrance%20signage%20company%20name%20board%20professional%20exterior%20architecture&width=300&height=200&seq=fw4&orientation=landscape" },
          ].map(photo => (
            <div key={photo.label} className="rounded-lg overflow-hidden">
              <img src={photo.img} alt={photo.label} className="w-full h-24 object-cover object-top" />
              <p className="text-center text-xs text-gray-500 py-1.5 bg-gray-50">{photo.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function FactoryDashboardPage() {
  const [activeTab, setActiveTab] = useState<TabType>("overview");

  const tabs: { key: TabType; label: string; icon: string; gold?: boolean }[] = [
    { key: "overview", label: "数据概览", icon: "ri-dashboard-line" },
    { key: "products", label: "商品管理", icon: "ri-store-line" },
    { key: "orders", label: "订单管理", icon: "ri-shopping-bag-line" },
    { key: "payments", label: "货款状态", icon: "ri-money-cny-circle-line" },
    { key: "payout", label: "收款账户", icon: "ri-bank-card-line" },
    { key: "kol", label: "达人管理", icon: "ri-vip-crown-line", gold: true },
    { key: "profile", label: "工厂资料", icon: "ri-building-line" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="工厂管理后台 | 商品订单货款管理 | 皇冠鸿运商城"
        description="皇冠鸿运商城工厂管理后台，管理商品、订单、货款状态、收款账户、达人合作，查看数据概览。"
        keywords="工厂后台,商品管理,订单管理,货款托管,皇冠鸿运商城"
        canonical="/factory-dashboard"
        jsonLd={factoryDashboardJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 工厂管理后台</span>
          <div className="flex items-center gap-4">
            <a href="/" className="hover:underline cursor-pointer flex items-center gap-1"><i className="ri-home-line"></i> 返回首页</a>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 to-orange-500 text-white px-4 py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl text-white text-2xl font-bold flex-shrink-0">
              极
            </div>
            <div>
              <h1 className="font-bold text-xl">极光户外装备有限公司</h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                  <i className="ri-verified-badge-line"></i> 已认证工厂
                </span>
                <span className="bg-white/20 text-white text-xs px-2 py-0.5 rounded-full">
                  <i className="ri-vip-crown-line mr-1"></i>付费会员 · 有效期至 2027-01-15
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/factory-quotes"
              className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap flex items-center gap-1.5 transition-colors"
            >
              <i className="ri-file-list-3-line"></i> 查看买家报价
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <div className="lg:w-52 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 text-sm transition-colors cursor-pointer whitespace-nowrap border-b border-gray-50 last:border-0 ${
                    activeTab === tab.key
                      ? tab.gold ? "bg-amber-50 text-amber-600 font-medium" : "bg-red-50 text-red-500 font-medium"
                      : tab.gold ? "text-amber-500 hover:bg-amber-50" : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={tab.icon}></i>
                  </div>
                  {tab.label}
                  {tab.gold && <span className="ml-auto text-xs bg-amber-100 text-amber-600 px-1.5 py-0.5 rounded-full font-bold">NEW</span>}
                </button>
              ))}
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {activeTab === "overview" && <OverviewTab />}
            {activeTab === "products" && <ProductsTab />}
            {activeTab === "orders" && <OrdersTab />}
            {activeTab === "payments" && <PaymentsTab />}
            {activeTab === "payout" && <PayoutTab />}
            {activeTab === "kol" && <KolTab />}
            {activeTab === "profile" && <ProfileTab />}
          </div>
        </div>
      </div>
    </div>
  );
}
